import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/20/solid';
import { fadeIn, slideUp } from '../../lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useFeedback } from '../../hooks/learner/useFeedback';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useModuleTimer } from '../../hooks/learner/useModuleTimer';
import { getEnrolmentAvailability } from '../../utils/enrolmentAvailability';
import { getFeedbackViewEntry } from '../../components/modules/views/registry';
import LearnerModuleHeader from '../../components/learner/LearnerModuleHeader';
import ModuleViewContainer from '../../components/modules/views/ModuleViewContainer';
import type { components } from '../../types/learnerApi';
import type { FeedbackAnswer } from '../../components/modules/views/registry';

type FeedbackModule = components['schemas']['FeedbackModule'];
type FeedbackItem = components['schemas']['FeedbackItem'];

const FeedbackPage: React.FC = () => {
  const { assignmentId, sectionId = '', moduleId = '' } = useParams<{
    assignmentId?: string;
    sectionId: string;
    moduleId: string;
  }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const routes = useCourseRoutes(id);

  const { data: course } = useCourse(id);
  const { existingFeedback, submit } = useFeedback(id, moduleId);

  // Record time spent on this feedback module, flushed on navigation / tab-close.
  useModuleTimer(id, moduleId);

  // Date-window gate: bounce deep-links back to the detail page when the
  // enrolment hasn't opened yet or has already ended.
  useEffect(() => {
    if (!course) return;
    const availability = getEnrolmentAvailability(course.assignment, {
      completed: course.progress?.bucket === 'completed',
    });
    if (availability.kind !== 'available') {
      navigate(routes.base, { replace: true });
    }
  }, [course, navigate, routes.base]);

  const { feedbackModule, sectionTitle } = useMemo(() => {
    for (const s of course?.sections ?? []) {
      for (const m of s.modules ?? []) {
        if (m.id === moduleId) {
          return {
            feedbackModule: m as FeedbackModule,
            sectionTitle: s.title || 'Section',
          };
        }
      }
    }
    return { feedbackModule: undefined, sectionTitle: 'Section' };
  }, [course, moduleId]);

  const items: FeedbackItem[] = feedbackModule?.details?.items ?? [];
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.order_index - b.order_index),
    [items]
  );

  // Next-module pointer so the thank-you screen can advance.
  const flat = (course?.sections ?? [])
    .slice()
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .flatMap((s) =>
      (s.modules ?? [])
        .slice()
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .map((m) => ({ module: m, sectionId: s.id ?? '' }))
    );
  const currentIdx = flat.findIndex((entry) => entry.module.id === moduleId);
  const nextEntry =
    currentIdx >= 0 && currentIdx < flat.length - 1 ? flat[currentIdx + 1] : null;
  const nextModule = nextEntry?.module ?? null;
  const goNext = () => {
    if (nextEntry?.module.id && nextEntry.sectionId) {
      navigate(
        `${routes.base}/sections/${nextEntry.sectionId}/modules/${nextEntry.module.id}`
      );
    } else {
      navigate(`${routes.base}/done`);
    }
  };

  // Course-level percent for the LearnerModuleHeader, mirroring the eval page.
  const completedCount = flat.filter(
    (f) =>
      ((f.module as { progress?: { status?: string } }).progress?.status ?? '') === 'completed'
  ).length;
  const coursePercent = flat.length > 0 ? (completedCount / flat.length) * 100 : 0;

  const [answers, setAnswers] = useState<Record<string, FeedbackAnswer>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seed from existing submission
  useEffect(() => {
    if (existingFeedback.data?.value?.items) {
      const seeded: Record<string, FeedbackAnswer> = {};
      for (const [id, val] of Object.entries(existingFeedback.data.value.items)) {
        seeded[id] = val as FeedbackAnswer;
      }
      setAnswers(seeded);
    }
  }, [existingFeedback.data]);

  // If already submitted, show thank-you
  useEffect(() => {
    if (existingFeedback.data?.submitted_at) {
      setSubmitted(true);
    }
  }, [existingFeedback.data]);

  const handleChange = (itemId: string, value: FeedbackAnswer) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  };

  const allRequiredAnswered = sortedItems.every((it) => {
    if (!it.required) return true;
    const { validate } = getFeedbackViewEntry(it.type);
    return validate(answers[it.id], it);
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        items: sortedItems.map((it) => ({
          item_id: it.id,
          value: answers[it.id] ?? {},
        })),
      };
      await submit.mutateAsync(payload);
      setSubmitted(true);
      toast.success('Feedback submitted!');
    } catch {
      toast.error('Could not submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Thank-you state ────────────────────────────────────────────────────

  if (submitted) {
    return (
      <motion.div
        {...slideUp}
        className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
      >
        <LearnerModuleHeader
          backTo={`${routes.base}/sections/${sectionId}/modules`}
          percent={coursePercent}
          sectionTitle={sectionTitle}
          showBack={false}
        />
        <div className="flex-1 flex flex-col min-h-0 pb-2">
          <ModuleViewContainer padding="px-4 pb-4">
            <div className="flex-1 flex flex-col items-center justify-center text-center px-3 sm:px-4 py-8 gap-3">
              <CheckCircleIcon className="w-14 h-14 text-success-500" />
              <h2 className="text-xl sm:text-2xl font-bold text-text-primary">
                Thank you for your feedback!
              </h2>
              <p className="text-sm text-text-secondary">
                Your responses have been recorded.
              </p>
            </div>
            <div className="flex items-center justify-end gap-4 px-3 sm:px-4 pb-3 sm:pb-4">
              <NextPill
                onClick={goNext}
                label={nextModule ? 'Continue' : 'Finish course'}
                finish={!nextModule}
              />
            </div>
          </ModuleViewContainer>
        </div>
      </motion.div>
    );
  }

  // ── Active feedback state ──────────────────────────────────────────────

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
    >
      <LearnerModuleHeader
        backTo={`${routes.base}/sections/${sectionId}/modules`}
        percent={coursePercent}
        sectionTitle={sectionTitle}
        showBack={false}
      />

      <div className="flex-1 flex flex-col min-h-0 pb-2">
        <ModuleViewContainer padding="px-4 pb-4">
          {feedbackModule?.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {feedbackModule.title}
              </h2>
              {feedbackModule.details?.anonymous && (
                <p className="text-xs text-text-secondary mt-1">
                  Responses are collected anonymously.
                </p>
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-3 sm:px-4 pb-3 sm:pb-4 space-y-4">
            {sortedItems.length === 0 ? (
              <p className="text-text-secondary text-sm italic">No questions available.</p>
            ) : (
              sortedItems.map((item, idx) => {
                const { View } = getFeedbackViewEntry(item.type);
                return (
                  <motion.div
                    key={item.id}
                    {...fadeIn}
                    className="rounded-2xl bg-surface-primary border-2 border-dashed border-primary-300 dark:border-primary-700 p-5 sm:p-6 space-y-4"
                  >
                    {/* "Question N" pill — light purple bg with a
                        purple→orange gradient text fill. */}
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-100/70 dark:bg-primary-900/30">
                      <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-secondary">
                        Question {idx + 1}
                      </span>
                    </span>
                    <p className="text-sm sm:text-base text-text-primary leading-snug">
                      {item.config.question}
                      {item.required && (
                        <span className="text-error-500 ml-1" aria-hidden="true">
                          *
                        </span>
                      )}
                    </p>
                    <View
                      item={item}
                      mode="learner"
                      value={answers[item.id]}
                      onChange={(v) => handleChange(item.id, v as FeedbackAnswer)}
                      disabled={isSubmitting}
                    />
                  </motion.div>
                );
              })
            )}
          </div>

          {sortedItems.length > 0 && (
            <div className="flex items-center justify-end gap-4 px-3 sm:px-4 pt-4 sm:pt-5 pb-3 sm:pb-4">
              <NextPill
                onClick={handleSubmit}
                label={isSubmitting ? 'Submitting…' : 'Submit Feedback'}
                disabled={!allRequiredAnswered}
                loading={isSubmitting}
              />
            </div>
          )}
        </ModuleViewContainer>
      </div>
    </motion.div>
  );
};

export default FeedbackPage;

// ── Sub-components ──────────────────────────────────────────────────────

interface NextPillProps {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  loading?: boolean;
  finish?: boolean;
}

const NextPill: React.FC<NextPillProps> = ({ onClick, label, disabled, loading, finish }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    aria-busy={loading}
    className={`inline-flex items-center justify-center gap-2 min-w-[151px] h-9 px-4 rounded-full text-sm font-bold shrink-0 active:scale-[0.97] transition-[background-color,transform,opacity] duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:brightness-110 shadow-[0_2px_8px_rgba(105,72,170,0.25)] ${ finish ? 'bg-gradient-to-t from-success-600 to-success-500' : 'bg-gradient-to-t from-primary-700 to-primary-500' }`}
  >
    {finish ? <CheckCircleIcon className="w-4 h-4" /> : null}
    {label}
    {!finish ? <ArrowRightIcon className="w-4 h-4" /> : null}
  </button>
);
